import { compileStyles } from 'lib/styled-helpers'
import { objectHasProperty } from 'lib/typescript-helpers'
import { useResizeObserver } from 'lib/use-resize-observer'
import React, { FC, useEffect, useLayoutEffect, useRef, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { Icon } from 'ui/icon'

import { getProjectName } from '../../lib/get-project-name'

export type Props = {
  title: string
  collapsed?: boolean
  hidden?: boolean
  hideCollapse?: boolean
  isHideCollapseFilterSection?: boolean
  newVersion: boolean
  isArrows: boolean
}

export const Collapse: FC<Props> = ({
  title,
  collapsed = true,
  children,
  hidden,
  hideCollapse,
  isHideCollapseFilterSection = false,
  newVersion,
  isArrows = true
}) => {
  const theme = useTheme()
  const [isCollapsed, setIsCollapsed] = useState(collapsed)
  const [colorArrowCollapse, setColorArrowCollapse] = useState(theme.palette.brand.primary)
  const [height, setHeight] = useState<null | number>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const [scrollHeight, setScrollHeight] = useState<null | number>(null)

  useResizeObserver(contentRef, (entry: ResizeObserverEntry) => {
    if (objectHasProperty(entry.target, 'offsetParent') && !entry.target.offsetParent) {
      setScrollHeight(null)
    } else {
      setScrollHeight(entry.target.scrollHeight)
    }
  })

  const setIconColor = () => {
    if (getProjectName() === 'generic_v2') return '#E4E4E4'
    return colorArrowCollapse
  }

  useLayoutEffect(() => {
    if (!contentRef.current) return

    if (isCollapsed) {
      if (hideCollapse || isHideCollapseFilterSection) {
        setHeight(scrollHeight)
      } else {
        setHeight(0)
      }
    } else {
      setHeight(scrollHeight)
    }
  }, [isCollapsed, scrollHeight])

  useEffect(() => {
    if (getProjectName() === 'tank') {
      setColorArrowCollapse(theme.palette.white)
    } else if (getProjectName() === 'ora') {
      setColorArrowCollapse(theme.palette.black)
    }
  }, [])

  return (
    <CollapseWrapper>
      {!hideCollapse && (
        <TitleWrapper
          newVersion={newVersion}
          isHideCollapseFilterSection={isHideCollapseFilterSection}
          onClick={() => setIsCollapsed((isCollapsed) => !isCollapsed)}
        >
          <Title isHideCollapseFilterSection={isHideCollapseFilterSection} newVersion={newVersion}>
            {title}
          </Title>
          {!isHideCollapseFilterSection && (
            <Arrow isCollapsed={isCollapsed}>
              {(getProjectName() !== 'generic_v2' || isArrows) && (
                <Icon
                  name={getProjectName() === 'generic_v2' ? 'arrow-down-generic-v2' : 'arrow-down'}
                  color={setIconColor()}
                />
              )}
              {getProjectName() === 'generic_v2' && isCollapsed && newVersion && (
                <Icon name="zzzzzz-plus" color={colorArrowCollapse} />
              )}
              {getProjectName() === 'generic_v2' && !isCollapsed && newVersion && (
                <Icon name="zzzzzz-minus" color={colorArrowCollapse} />
              )}
            </Arrow>
          )}
        </TitleWrapper>
      )}

      <ChildrenWrapper height={height} newVersion={newVersion}>
        <div ref={contentRef}>{children}</div>
      </ChildrenWrapper>
    </CollapseWrapper>
  )
}

const CollapseWrapper = styled.div`
  width: 100%;
  overflow: ${() => getProjectName() !== 'avito' && 'hidden'};
`

const TitleWrapper = styled.div<{ isHideCollapseFilterSection: boolean; newVersion: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme, newVersion }) => {
    if (getProjectName() === 'generic_v2' && newVersion) return '10px 20px'
    return `${theme.spacing[5]} ${theme.spacing[4]}`
  }};
  padding-bottom: ${({ isHideCollapseFilterSection }) => isHideCollapseFilterSection && 0};
  cursor: pointer;
  font-weight: ${({ isHideCollapseFilterSection }) => isHideCollapseFilterSection && 'bold'};
  background: ${({ theme, newVersion }) => {
    if (getProjectName() === 'generic_v2' && newVersion) return theme.palette.grey[800]
    return 'none'
  }};
  border-radius: ${({ newVersion }) => {
    if (getProjectName() === 'generic_v2' && newVersion) return '8px'
    return 'none'
  }};
`

const Title = styled[getProjectName() === 'mini' ? 'h6' : 'p']<{
  isHideCollapseFilterSection: boolean
  newVersion: boolean
}>`
  margin: 0;
  ${({ theme }) => `
    color: ${theme.palette.grey[900]};
    ${getProjectName() === 'mini' ? compileStyles(theme.textStyles.h6) : compileStyles(theme.textStyles.p1)}
  `};
  color: ${({ theme }) => {
    if (getProjectName() === 'tank') return theme.palette.white
    if (getProjectName() === 'generic_v2') return theme.palette.grey[100]
    return theme.palette.grey[900]
  }};
  font-weight: ${({ isHideCollapseFilterSection }) => isHideCollapseFilterSection && 'bold'};
  font-weight: ${({}) => getProjectName() === 'haval' && 500};
  font-size: ${({ newVersion }) => {
    if (getProjectName() === 'haval') return '1.4rem'
    if (getProjectName() === 'generic_v2' && newVersion) return '2rem'
    return null
  }};
  text-transform: ${({ newVersion }) => {
    if (getProjectName() === 'ora') return 'uppercase'
    if (getProjectName() === 'generic_v2' && newVersion) return 'uppercase'
    return 'none'
  }};

  @media screen and (max-width: 768px) {
    font-size: ${({ newVersion }) => {
      if (getProjectName() === 'generic_v2' && newVersion) {
        return '1.6rem'
      }
      return '1.6rem'
    }};
  }
`

const Arrow = styled.div<{ isCollapsed: boolean }>`
  transform: ${({ isCollapsed }) => (isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)')};
  transition: 0.3s linear;

  & svg {
    width: ${({}) => getProjectName() === 'generic_v2' && '16px'};
    height: ${({}) => getProjectName() === 'generic_v2' && '16px'};
  }
`

const Path = styled.path`
  fill: ${({ theme }) => theme.palette.brand.primary};
`

const ChildrenWrapper = styled.div<{ height: number | null; newVersion: boolean }>`
  height: ${({ height }) => {
    return height === null ? 'auto' : `${height}px`
  }};
  max-height: ${() => getProjectName() === 'avito' && '300px'};
  box-sizing: border-box;
  transition: ${() => (getProjectName() === 'avito' ? 'none' : 'height 0.25s')};
  overflow: hidden;
  overflow-y: ${() => getProjectName() === 'avito' && 'auto'};
  transform: ${() => getProjectName() === 'avito' && 'translateZ(0)'};

  div {
    display: ${({ newVersion }) => getProjectName() === 'generic_v2' && newVersion && 'flex'};
    justify-content: ${({ newVersion }) => getProjectName() === 'generic_v2' && newVersion && 'flex-end'};
  }
`
